import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { publicApi } from '../lib/api';
import PageSEO from '../components/PageSEO';
import { SafePhone, SafeEmail, SafeAddress, usePhoneClick, useEmailClick } from '../components/SafeContact';

export const ContactPage = () => {
  const handlePhoneClick = usePhoneClick();
  const handleEmailClick = useEmailClick();
  const infoPanelRef = useRef(null);

  useEffect(() => {
    const el = infoPanelRef.current;
    if (!el) return;
    const block = (e) => { e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('copy', block, true);
    el.addEventListener('cut', block, true);
    el.addEventListener('dragstart', block, true);
    el.addEventListener('selectstart', block, true);
    el.addEventListener('contextmenu', block, true);
    return () => {
      el.removeEventListener('copy', block, true);
      el.removeEventListener('cut', block, true);
      el.removeEventListener('dragstart', block, true);
      el.removeEventListener('selectstart', block, true);
      el.removeEventListener('contextmenu', block, true);
    };
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: '', // honeypot
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await publicApi.submitContact(formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Çok fazla istek gönderildi. Lütfen birkaç dakika sonra tekrar deneyin.');
      } else if (err.response?.status === 422) {
        setError('Lütfen tüm alanları doğru şekilde doldurun.');
      } else {
        setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="page-transition">
      <PageSEO
        title="İletişim"
        description="VE PERA İnşaat ile iletişime geçin. Ücretsiz ön değerlendirme randevusu alın. Telefon ve e-posta bilgilerimiz."
        path="/iletisim"
      />
      {/* Hero - Editorial */}
      <section className="pt-32 lg:pt-40 pb-20 bg-[#f2f2f7]">
        <div className="container-wide">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7">
              <span className="overline mb-6 block">İletişim</span>
              <h1
                className="text-[clamp(2.5rem,6vw,4.5rem)] leading-[1] tracking-[-0.03em]"
                style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
              >
                Birlikte
                <br />
                <span className="italic font-light text-[#8e8e93]">konuşalım</span>
              </h1>
            </div>
            <div className="lg:col-span-4 lg:col-start-9 flex items-end">
              <p
                className="text-[#8e8e93] leading-[1.9] text-[15px]"
                style={{ fontFamily: "var(--font-body)" }}
              >
                Sorularınız için bize ulaşın veya ücretsiz ön değerlendirme randevusu alın. Uzman ekibimiz size yardımcı olmak için hazır.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact - Split layout */}
      <section data-testid="contact-content" className="bg-[#f2f2f7]">
        <div className="grid lg:grid-cols-2">
          {/* Left - Form */}
          <div className="px-6 lg:px-16 xl:px-24 py-16 lg:py-24">
            {success ? (
              <div data-testid="contact-success" className="flex flex-col items-start">
                <div className="w-16 h-[1px] bg-[#041f60] mb-10"></div>
                <CheckCircle className="w-12 h-12 text-[#041f60] mb-8" strokeWidth={1} />
                <h3
                  className="text-[clamp(1.5rem,3vw,2rem)] leading-[1.2] tracking-[-0.02em] mb-4"
                  style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                >
                  Mesajınız Alındı
                </h3>
                <p
                  className="text-[#8e8e93] leading-[1.8] text-[15px] mb-10"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  En kısa sürede size dönüş yapacağız. Teşekkür ederiz.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  data-testid="contact-new-message"
                  className="link-line text-[#1a1a1a] hover:text-[#b87333] transition-colors"
                >
                  Yeni Mesaj Gönder
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <span className="overline mb-8 block">Mesaj Gönderin</span>

                <div className="space-y-0">
                  {/* Name */}
                  <div className="py-5 border-b border-[#d1d1d6]">
                    <label
                      className="block text-[10px] tracking-[0.2em] uppercase text-[#8e8e93]/60 mb-2"
                      style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                    >
                      Adınız Soyadınız
                    </label>
                    <input
                      data-testid="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-0 p-0 text-[16px] text-[#1a1a1a] placeholder:text-[#8e8e93]/30 focus:ring-0 focus:outline-none"
                      style={{ fontFamily: "var(--font-body)" }}
                      placeholder="Adınız Soyadınız"
                    />
                  </div>

                  {/* Email & Phone */}
                  <div className="grid sm:grid-cols-2 gap-0 sm:gap-12">
                    <div className="py-5 border-b border-[#d1d1d6]">
                      <label
                        className="block text-[10px] tracking-[0.2em] uppercase text-[#8e8e93]/60 mb-2"
                        style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                      >
                        E-posta
                      </label>
                      <input
                        data-testid="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-0 p-0 text-[16px] text-[#1a1a1a] placeholder:text-[#8e8e93]/30 focus:ring-0 focus:outline-none"
                        style={{ fontFamily: "var(--font-body)" }}
                        placeholder="ornek@email.com"
                      />
                    </div>
                    <div className="py-5 border-b border-[#d1d1d6]">
                      <label
                        className="block text-[10px] tracking-[0.2em] uppercase text-[#8e8e93]/60 mb-2"
                        style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                      >
                        Telefon
                      </label>
                      <input
                        data-testid="contact-phone"
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-0 p-0 text-[16px] text-[#1a1a1a] placeholder:text-[#8e8e93]/30 focus:ring-0 focus:outline-none"
                        style={{ fontFamily: "var(--font-body)" }}
                        placeholder="0532 000 00 00"
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="py-5 border-b border-[#d1d1d6]">
                    <label
                      className="block text-[10px] tracking-[0.2em] uppercase text-[#8e8e93]/60 mb-2"
                      style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                    >
                      Konu
                    </label>
                    <input
                      data-testid="contact-subject"
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-0 p-0 text-[16px] text-[#1a1a1a] placeholder:text-[#8e8e93]/30 focus:ring-0 focus:outline-none"
                      style={{ fontFamily: "var(--font-body)" }}
                      placeholder="Konu başlığı"
                    />
                  </div>

                  {/* Message */}
                  <div className="py-5 border-b border-[#d1d1d6]">
                    <label
                      className="block text-[10px] tracking-[0.2em] uppercase text-[#8e8e93]/60 mb-2"
                      style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                    >
                      Mesajınız
                    </label>
                    <textarea
                      data-testid="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full bg-transparent border-0 p-0 text-[16px] text-[#1a1a1a] placeholder:text-[#8e8e93]/30 focus:ring-0 focus:outline-none resize-none"
                      style={{ fontFamily: "var(--font-body)" }}
                      placeholder="Mesajınızı yazın..."
                    />
                  </div>
                </div>

                {/* Honeypot - invisible to humans, bots fill it */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {error && (
                  <p
                    data-testid="contact-error"
                    className="text-red-600 text-[13px] mt-4"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    {error}
                  </p>
                )}

                <div className="mt-10">
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="btn-primary disabled:opacity-50"
                  >
                    {loading ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Right - Info on dark background — anti-scraping hardened */}
          <div
            ref={infoPanelRef}
            className="bg-[#1a1a1a] px-6 lg:px-16 xl:px-24 py-16 lg:py-24 flex flex-col justify-between select-none"
            style={{ WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none' }}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            draggable={false}
          >
            <div>
              <span
                className="text-[10px] tracking-[0.3em] uppercase text-[#f2f2f7]/70 mb-10 block"
                style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
              >
                İletişim Bilgileri
              </span>

              <div className="space-y-10">
                <div>
                  <span
                    className="text-[11px] tracking-[0.15em] uppercase text-[#d1d1d6]/40 mb-3 block"
                    style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                  >
                    Telefon
                  </span>
                  <button onClick={handlePhoneClick} className="cursor-pointer hover:opacity-80 transition-opacity">
                    <SafePhone
                      className="text-[#f2f2f7] text-[18px] leading-[1.8] block"
                      style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                    />
                  </button>
                </div>

                <div>
                  <span
                    className="text-[11px] tracking-[0.15em] uppercase text-[#d1d1d6]/40 mb-3 block"
                    style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                  >
                    E-posta
                  </span>
                  <button onClick={handleEmailClick} className="cursor-pointer hover:opacity-80 transition-opacity">
                    <SafeEmail
                      className="text-[#f2f2f7] text-[18px] block"
                      style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                    />
                  </button>
                </div>

                <div>
                  <span
                    className="text-[11px] tracking-[0.15em] uppercase text-[#d1d1d6]/40 mb-3 block"
                    style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                  >
                    Adres
                  </span>
                  <SafeAddress
                    className="text-[#f2f2f7] text-[18px] leading-[1.6] block"
                    style={{ fontFamily: "var(--font-heading)", fontWeight: 400 }}
                  />
                </div>

                <div>
                  <span
                    className="text-[11px] tracking-[0.15em] uppercase text-[#d1d1d6]/40 mb-3 block"
                    style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                  >
                    Çalışma Saatleri
                  </span>
                  <p
                    className="text-[#d1d1d6]/60 text-[15px] leading-[1.9]"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    Pazartesi — Cuma: 09:00 – 18:00
                    <br />Cumartesi: 10:00 – 14:00
                  </p>
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="mt-16 pt-10 border-t border-[#8e8e93]/20">
              <a
                href="https://wa.me/902166068027?text=Merhaba,%20VE%20PERA%20%C4%B0n%C5%9Faat%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-cta"
                className="group inline-flex items-center gap-4"
              >
                <span className="w-12 h-12 flex items-center justify-center bg-[#25D366]">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </span>
                <span
                  className="text-[12px] tracking-[0.12em] uppercase text-[#d1d1d6]/70 group-hover:text-[#25D366] transition-colors"
                  style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                >
                  WhatsApp ile Yazın
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
